import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
  Stack,
  Grid,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PhoneIcon from "@mui/icons-material/Phone";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import LanguageIcon from "@mui/icons-material/Language";
import { YouTube } from "@mui/icons-material";
import { FaSnapchat } from "react-icons/fa";
import { FaImdb } from "react-icons/fa";
import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { FaTiktok } from "react-icons/fa";
import { useTheme, useMediaQuery } from "@mui/material";
/* ---------------- HELPERS ---------------- */

const sanitizePhone = (value = "") => String(value).replace(/[^\d+]/g, "");

const normalizeUrl = (url = "") =>
  url.startsWith("http") ? url : `https://${url}`;

export default function ContactDialog({
  open,
  onClose,
  contact = {},
  headings = [],
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [reps, setReps] = useState({});

  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId && storedUserId !== "undefined") {
      setUserId(storedUserId);
    }
  }, []);

  const sanitizePhone = (value = "") =>
    String(value).replace(/[^\d+]/g, "");

  const normalizeUrl = (url = "") =>
    url.startsWith("http") ? url : `https://${url}`;

  const mappedContact = useMemo(
    () => ({
      phone: contact.phone || contact.primaryContactNumber || "",
      email: contact.email || "",
      faceBook: contact.faceBook || "",
      twitter: contact.twitter || "",
      instagram: contact.instagram || "",
      tikTok: contact.tikTok || "",
      personalWebSite: contact.personalWebSite || "",
      secondary: contact.secondaryPhone || "",
      imdb: contact.imdb || "",
      youTube: contact.youTube || "",
      snapChat: contact.snapChat || "",
    }),
    [contact]
  );

  useEffect(() => {
    if (!userId || !headings.length) return;

    const fetchReps = async () => {
      const initial = {};
      for (const h of headings) {
        try {
          const res = await api.get(
            ENDPOINTS.getRepresentationInformation(userId, h)
          );
          const info = res?.data?.[0] || {};
          initial[h] = {
            nameofCompany: info.nameofCompany || "",
            representativeName: info.representativeName || "",
            representativeContact: sanitizePhone(
              info.representativeContact || ""
            ),
          };
        } catch {}
      }
      setReps(initial);
    };

    fetchReps();
  }, [userId, headings]);

  const paymentLinks = [1, 2, 3]
    .map((i) => contact[`paymentLink${i}`] || contact[`paymentlink${i}`])
    .filter(Boolean);

  const socialLinks = [
    mappedContact.instagram,
    mappedContact.twitter,
    mappedContact.faceBook,
    mappedContact.personalWebSite,
    mappedContact.snapChat,
    mappedContact.imdb,
    mappedContact.youTube,
  ].filter(Boolean);

  const hasPersonalContact =
    mappedContact.phone || mappedContact.email;

  const repEntries = headings
    .map((h) => reps[h])
    .filter(
      (info) =>
        info &&
        (info.nameofCompany ||
          info.representativeName ||
          info.representativeContact)
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
    <DialogTitle
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 700,
  }}
>
  <Typography variant="p" fontWeight={700}>
    Contact Information
  </Typography>

  <Link
    component="button"
    underline="hover"
    onClick={() => {
      onClose();
      navigate("/profile");
    }}
    sx={{ fontWeight: 400, fontSize: 14 }}
  >
    Edit Contact
  </Link>
</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={4}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {hasPersonalContact && (
                <Box>
                  <Typography fontWeight={700} fontSize={18}>
                    PERSONAL CONTACT
                  </Typography>

                  <Stack spacing={1} mt={1}>
                    {mappedContact.phone && (
                      <Typography sx={{ wordBreak: "break-word" }}>
                        <strong>Primary Phone:</strong>{" "}
                        {mappedContact.phone}
                       <div> <strong>Secondary Phone:</strong>{" "}
                        {mappedContact.secondary &&
                          `${mappedContact.secondary}`}</div>
                          
                      </Typography>
                    )}
                    {mappedContact.email && (
                      <Typography sx={{ wordBreak: "break-word" }}>
                        <strong>Email:</strong>{" "}
                        <Link
                          href={`mailto:${mappedContact.email}`}
                        >
                          {mappedContact.email}
                        </Link>
                      </Typography>
                    )}

                    {mappedContact.personalWebSite && (
                      <Typography sx={{ wordBreak: "break-word" }}>
                        <strong>Website:</strong>{" "}
                        <Link
                          href={normalizeUrl(
                            mappedContact.personalWebSite
                          )}
                          target="_blank"
                        >
                          {normalizeUrl(
                            mappedContact.personalWebSite
                          )}
                        </Link>
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              {repEntries.length > 0 && (
                <Box>
                  <Typography fontWeight={700} fontSize={18}>
                    REPRESENTATION
                  </Typography>

                  <Stack spacing={2} mt={1}>
                    {headings.map((h) => {
                      const info = reps[h];
                      if (
                        !info ||
                        (!info.nameofCompany &&
                          !info.representativeContact)
                      )
                        return null;

                      return (
                        <Box key={h}>
                          <Typography fontWeight={600}>
                            {h}
                          </Typography>
                          {info.nameofCompany && (
                            <Typography>
                              {info.nameofCompany}
                            </Typography>
                          )}
                          {info.representativeName && (
                            <Typography>
                              Name: {info.representativeName}
                            </Typography>
                          )}
                          {info.representativeContact && (
                            <Typography>
                              Contact:{" "}
                              {info.representativeContact}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Grid>

          {/* RIGHT COLUMN */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {paymentLinks.length > 0 && (
                <Box>
                  <Typography fontWeight={700} fontSize={18}>
                    PAYMENT
                  </Typography>

                  <Stack spacing={1.5} mt={1}>
                    {paymentLinks.map((link, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ flexWrap: "wrap" }}
                      >
                        <AccountBalanceWalletIcon fontSize="small" />
                        <Link
                          href={normalizeUrl(link)}
                          target="_blank"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {normalizeUrl(link)}
                        </Link>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {socialLinks.length > 0 && (
                <Box>
                  <Typography fontWeight={700} fontSize={18}>
                    FOLLOW
                  </Typography>

                  <Stack spacing={1.5} mt={1}>
                    {[
                      { icon: <InstagramIcon />, value: mappedContact.instagram },
                      { icon: <FacebookIcon />, value: mappedContact.faceBook },
                      { icon: <FaTiktok size={18} />, value: mappedContact.tikTok },
                      { icon: <YouTube />, value: mappedContact.youTube },
                      { icon: <FaImdb size={18} />, value: mappedContact.imdb },
                      { icon: <FaSnapchat size={18} />, value: mappedContact.snapChat },
                    ]
                      .filter((item) => item.value)
                      .map((item, index) => (
                        <Stack
                          key={index}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "wrap" }}
                        >
                          {item.icon}
                          <Link
                            href={normalizeUrl(item.value)}
                            target="_blank"
                            sx={{ wordBreak: "break-word" }}
                          >
                            {normalizeUrl(item.value)}
                          </Link>
                        </Stack>
                      ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
